<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">門市業績報表</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleExport">
          <i class="ri-download-line"></i>
          匯出 Excel
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>開始日期</label>
        <input
          v-model="filters.startDate"
          type="date"
          class="form-control"
          required
        />
      </div>
      <div class="filter-group">
        <label>結束日期</label>
        <input
          v-model="filters.endDate"
          type="date"
          class="form-control"
          required
        />
      </div>
      <div class="filter-group">
        <label>期間</label>
        <select v-model="filters.period" class="form-control">
          <option value="day">每日</option>
          <option value="week">每週</option>
          <option value="month">每月</option>
          <option value="year">每年</option>
        </select>
      </div>
      <button class="btn btn-primary" @click="fetchData">查詢</button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>門市名稱</th>
            <th>總營收</th>
            <th>訂單數</th>
            <th>平均客單價</th>
            <th>會員數</th>
            <th>新增會員</th>
            <th>營收排名</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!summaries || summaries.length === 0">
            <td colspan="7" class="empty-state">沒有找到資料</td>
          </tr>
          <tr v-for="item in summaries" :key="item.storeId">
            <td>{{ item.storeName }}</td>
            <td>{{ formatCurrency(item.totalRevenue) }}</td>
            <td>{{ item.totalOrders }}</td>
            <td>{{ formatCurrency(item.averageOrderValue) }}</td>
            <td>{{ item.totalMembers }}</td>
            <td>{{ item.newMembers }}</td>
            <td>{{ item.revenueRank }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { reportsService } from '@/api/modules/reports.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'SalesReport',
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const summaries = ref([]);
    const filters = ref({
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      period: 'day'
    });

    const fetchData = async () => {
      if (!filters.value.startDate || !filters.value.endDate) {
        showError('錯誤', '請選擇開始日期和結束日期');
        return;
      }

      try {
        loading.value = true;
        error.value = null;
        const response = await reportsService.getStoreSalesReport(filters.value);
        if (response.success) {
          summaries.value = response.data.summaries || [];
        } else {
          error.value = response.message || '取得資料失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得資料失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleExport = async () => {
      if (!filters.value.startDate || !filters.value.endDate) {
        showError('錯誤', '請先選擇日期範圍');
        return;
      }

      try {
        loading.value = true;
        const params = {
          reportType: 'store-sales',
          format: 'excel',
          ...filters.value
        };
        const blob = await reportsService.exportReport(params);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `門市業績報表_${filters.value.startDate}_${filters.value.endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('成功', '報表已匯出');
      } catch (err) {
        showError('匯出失敗', err.response?.data?.message || err.message || '匯出失敗');
      } finally {
        loading.value = false;
      }
    };

    const formatCurrency = (value) => {
      if (!value) return '$0';
      return `$${parseInt(value).toLocaleString()}`;
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      summaries,
      filters,
      fetchData,
      handleExport,
      formatCurrency
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
</style>
